import React, { useState, useEffect } from 'react';
import './AccountDeletionModal.css';

const AccountDeletionModal = ({ isOpen, onClose, organizationId, organizationName, userRole }) => {
  const [deletionType, setDeletionType] = useState('end_of_billing');
  const [confirmationText, setConfirmationText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load deletion preview when modal opens
  useEffect(() => {
    if (isOpen && organizationId) {
      loadDeletionPreview();
    }
  }, [isOpen, organizationId]);

  const loadDeletionPreview = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081';
      const response = await fetch(`${apiUrl}/api/account-deletion/organization/deletion-preview/${organizationId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeletionPreview(data.deletionPreview);
      }
    } catch (error) {
      console.error('Failed to load deletion preview:', error);
    }
  };

  const handleDeleteOrganization = async () => {
    if (deletionType === 'immediate' && confirmationText !== organizationName) {
      setError('You must type the organization name exactly to confirm deletion');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081';
      const response = await fetch(`${apiUrl}/api/account-deletion/organization/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          organizationId,
          deletionType,
          confirmationText: deletionType === 'immediate' ? confirmationText : undefined
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (deletionType === 'immediate') {
          setSuccess('Organization has been deleted successfully. You will be redirected shortly.');
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        } else {
          setSuccess(`Organization deletion has been scheduled for ${new Date(data.deletionDate).toLocaleDateString()}. You will maintain access until then.`);
          setTimeout(() => {
            onClose();
            window.location.reload(); // Refresh to show updated status
          }, 3000);
        }
      } else {
        setError(data.message || 'Failed to delete organization');
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Delete organization error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setDeletionType('end_of_billing');
    setConfirmationText('');
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  // Check if user has permission to delete
  const canDelete = userRole === 'owner' || userRole === 'admin';

  if (!canDelete) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Account Deletion</h2>
            <button className="close-button" onClick={handleClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="permission-denied">
              <div className="warning-icon">⚠️</div>
              <h3>Permission Denied</h3>
              <p>Only organization owners and administrators can delete the organization.</p>
              <p>Your role: <strong>{userRole}</strong></p>
              <p>If you want to leave this organization, please contact your organization administrator.</p>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content deletion-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Delete Organization</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>
        
        <div className="modal-body">
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">❌</span>
              {error}
            </div>
          )}
          
          {success && (
            <div className="alert alert-success">
              <span className="alert-icon">✅</span>
              {success}
            </div>
          )}

          {!success && (
            <>
              <div className="danger-zone">
                <div className="warning-section">
                  <div className="warning-icon">⚠️</div>
                  <div>
                    <h3>This action cannot be undone!</h3>
                    <p>Deleting "<strong>{organizationName}</strong>" will permanently remove all data associated with this organization.</p>
                  </div>
                </div>

                {deletionPreview && (
                  <div className="deletion-preview">
                    <h4>What will be deleted:</h4>
                    <div className="data-summary">
                      <div className="data-item">
                        <span className="data-label">Organization Members:</span>
                        <span className="data-value">{deletionPreview.dataToDelete.members}</span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">Projects:</span>
                        <span className="data-value">{deletionPreview.dataToDelete.projects}</span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">Documents:</span>
                        <span className="data-value">{deletionPreview.dataToDelete.documents}</span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">Chat Conversations:</span>
                        <span className="data-value">{deletionPreview.dataToDelete.chats}</span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">Messages:</span>
                        <span className="data-value">{deletionPreview.dataToDelete.messages}</span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">Subscription:</span>
                        <span className="data-value">{deletionPreview.dataToDelete.stripeSubscriptions}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="deletion-options">
                  <h4>Choose deletion timing:</h4>
                  
                  <div className="option-group">
                    <label className={`option-card ${deletionType === 'end_of_billing' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="deletionType"
                        value="end_of_billing"
                        checked={deletionType === 'end_of_billing'}
                        onChange={(e) => setDeletionType(e.target.value)}
                      />
                      <div className="option-content">
                        <div className="option-title">Delete at end of billing period</div>
                        <div className="option-description">
                          Your organization will be scheduled for deletion at the end of your current billing cycle. 
                          You'll maintain full access until then.
                        </div>
                        <div className="option-badge recommended">Recommended</div>
                      </div>
                    </label>

                    <label className={`option-card ${deletionType === 'immediate' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="deletionType"
                        value="immediate"
                        checked={deletionType === 'immediate'}
                        onChange={(e) => setDeletionType(e.target.value)}
                      />
                      <div className="option-content">
                        <div className="option-title">Delete immediately</div>
                        <div className="option-description">
                          Your organization will be deleted right now. All access will be lost immediately, 
                          and you will forfeit any remaining subscription time.
                        </div>
                        <div className="option-badge immediate">Immediate</div>
                      </div>
                    </label>
                  </div>

                  {deletionType === 'immediate' && (
                    <div className="confirmation-section">
                      <label htmlFor="confirmationText">
                        To confirm immediate deletion, type the organization name exactly:
                      </label>
                      <div className="confirmation-requirement">
                        Type: <strong>{organizationName}</strong>
                      </div>
                      <input
                        id="confirmationText"
                        type="text"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        placeholder="Enter organization name"
                        className="confirmation-input"
                        autoComplete="off"
                      />
                    </div>
                  )}
                </div>

                {deletionPreview?.warnings && (
                  <div className="warnings-section">
                    <h4>Important warnings:</h4>
                    <ul className="warning-list">
                      {deletionPreview.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {!success && (
          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              className={`btn ${deletionType === 'immediate' ? 'btn-danger' : 'btn-warning'}`}
              onClick={handleDeleteOrganization}
              disabled={isLoading || (deletionType === 'immediate' && confirmationText !== organizationName)}
            >
              {isLoading ? 'Processing...' : 
               deletionType === 'immediate' ? 'Delete Now' : 'Schedule Deletion'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDeletionModal; 